import { UserFeedbackScreenCapture } from './types';

const MAX_CAPTURE_WIDTH = 1440;
const MAX_CAPTURE_HEIGHT = 1400;
const MAX_CAPTURE_DATA_URL_LENGTH = 5600000;
const JPEG_QUALITY = 0.72;

const getStylesheetText = () =>
  Array.from(document.styleSheets)
    .map(styleSheet => {
      try {
        return Array.from(styleSheet.cssRules)
          .map(rule => rule.cssText)
          .join('\n');
      } catch {
        return '';
      }
    })
    .filter(Boolean)
    .join('\n');

const replaceUnsupportedVisuals = (root: HTMLElement) => {
  root
    .querySelectorAll('canvas, iframe, video')
    .forEach(element => {
      const placeholder = document.createElement('div');
      placeholder.style.alignItems = 'center';
      placeholder.style.background = '#f1f5f9';
      placeholder.style.border = '1px dashed #94a3b8';
      placeholder.style.borderRadius = '10px';
      placeholder.style.color = '#475569';
      placeholder.style.display = 'flex';
      placeholder.style.font = '600 13px sans-serif';
      placeholder.style.justifyContent = 'center';
      placeholder.style.minHeight = '160px';
      placeholder.textContent = `${element.tagName.toLowerCase()} preview`;
      element.replaceWith(placeholder);
    });
};

const buildSnapshotMarkup = (
  width: number,
  height: number,
  scrollX: number,
  scrollY: number,
) => {
  const bodyClone = document.body.cloneNode(true) as HTMLElement;
  bodyClone
    .querySelectorAll('[data-feedback-launcher], .modal, .modal-backdrop')
    .forEach(element => element.remove());
  bodyClone.querySelectorAll('script').forEach(element => element.remove());
  replaceUnsupportedVisuals(bodyClone);

  const wrapper = document.createElement('div');
  wrapper.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
  wrapper.style.background = '#ffffff';
  wrapper.style.height = `${height}px`;
  wrapper.style.overflow = 'hidden';
  wrapper.style.width = `${width}px`;

  const style = document.createElement('style');
  style.textContent = `
    ${getStylesheetText()}
    html, body { margin: 0; width: ${width}px; min-height: ${height}px; overflow: hidden; }
    * { box-sizing: border-box; }
  `;

  const content = document.createElement('div');
  content.className = document.body.className;
  content.innerHTML = bodyClone.innerHTML;
  content.style.minHeight = `${document.documentElement.scrollHeight}px`;
  content.style.position = 'relative';
  content.style.transform = `translate(${-scrollX}px, ${-scrollY}px)`;
  content.style.width = `${document.documentElement.scrollWidth}px`;

  wrapper.appendChild(style);
  wrapper.appendChild(content);

  return new XMLSerializer().serializeToString(wrapper);
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to render page snapshot.'));
    image.src = src;
  });

export const captureCurrentPageSnapshot = async (): Promise<UserFeedbackScreenCapture | null> => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }

  const viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
  const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
  const scrollX = window.scrollX || window.pageXOffset || 0;
  const scrollY = window.scrollY || window.pageYOffset || 0;
  const width = Math.min(viewportWidth || MAX_CAPTURE_WIDTH, MAX_CAPTURE_WIDTH);
  const height = Math.min(viewportHeight || MAX_CAPTURE_HEIGHT, MAX_CAPTURE_HEIGHT);
  const scale = Math.min(
    1,
    width / Math.max(viewportWidth, 1),
    height / Math.max(viewportHeight, 1),
  );
  const outputWidth = Math.round(viewportWidth * scale);
  const outputHeight = Math.round(viewportHeight * scale);
  const markup = buildSnapshotMarkup(viewportWidth, viewportHeight, scrollX, scrollY);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${viewportWidth}" height="${viewportHeight}" viewBox="0 0 ${viewportWidth} ${viewportHeight}">
      <foreignObject width="100%" height="100%">${markup}</foreignObject>
    </svg>
  `;
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);

  try {
    const image = await loadImage(objectUrl);
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const context = canvas.getContext('2d');
    if (!context) return null;

    context.drawImage(image, 0, 0, outputWidth, outputHeight);
    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    if (dataUrl.length > MAX_CAPTURE_DATA_URL_LENGTH) return null;

    return {
      capturedAt: new Date().toISOString(),
      contentType: 'image/jpeg',
      dataUrl,
      fileName: `feedback-page-${Date.now()}.jpg`,
    };
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
